import { Product } from '@/data/replenishmentPlan/slice/replenishmentPlan.types.ts'
import { FC, useEffect, useState } from 'react'
import {
  Header,
  IconWrapper,
  Products,
  ProductWrapper,
  StyledDialog,
  Text,
  Title,
} from './ReplaceProductDialog.styles.ts'
import { useTranslation } from 'react-i18next'
import { ProductCard } from '@/ui/components/ProductCard'
import ArrowRightIcon from '@/core/assets/icons/arrow-right.svg'
import CloseIcon from '@/core/assets/icons/close.svg'
import { Box, Button, IconButton } from '@mui/material'
import LoadingButton from '@mui/lab/LoadingButton'
import { useReplaceProduct } from '@/data/replenishmentPlan/hooks/useReplaceProduct.ts'
import { useTrackReplenishmentPlanAnalyticsEvent } from '@/core/hooks/useTrackReplenishmentPlanAnalyticsEvent.ts'
import {
  DatagridActionsEvent,
  DatagridActionsType,
} from '@/core/analytics/events/replenishmentPlan/DatagridActionsEvent.ts'

interface Props {
  replaceFrom: Product | null
  replaceTo: Product | null
  onClose: () => void
  onComplete: () => void
}

const ReplaceProductDialog: FC<Props> = ({
  replaceFrom,
  replaceTo,
  onComplete,
  onClose,
}) => {
  const [replaceFromProduct, setReplaceFromProduct] = useState(replaceFrom)
  const [replaceToProduct, setReplaceToProduct] = useState(replaceTo)

  const { trackReplenishmentPlanAnalyticsEvent } =
    useTrackReplenishmentPlanAnalyticsEvent()

  useEffect(() => {
    if (replaceFrom) {
      setReplaceFromProduct(replaceFrom)
    }
  }, [replaceFrom])

  useEffect(() => {
    if (replaceTo) {
      setReplaceToProduct(replaceTo)
    }
  }, [replaceTo])

  const { t } = useTranslation()

  const { replaceProduct, isLoading } = useReplaceProduct()

  const onConfirm = async () => {
    trackReplenishmentPlanAnalyticsEvent(
      new DatagridActionsEvent({ type: DatagridActionsType.ReplaceConfirmed }),
    )

    try {
      await replaceProduct(replaceFrom!.id, replaceTo!.id)

      onComplete()
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <StyledDialog fullWidth open={Boolean(replaceTo)}>
      <Header>
        <Title>{t('replaceProductsDialog.title')}</Title>

        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Header>
      <Text>{t('replaceProductsDialog.text')}</Text>

      <Products>
        {replaceFromProduct && (
          <ProductWrapper>
            <ProductCard product={replaceFromProduct} />
          </ProductWrapper>
        )}

        <IconWrapper>
          <ArrowRightIcon />
        </IconWrapper>

        {replaceToProduct && (
          <ProductWrapper>
            <ProductCard product={replaceToProduct} />
          </ProductWrapper>
        )}
      </Products>

      <LoadingButton
        color="primary"
        variant="contained"
        loading={isLoading}
        onClick={onConfirm}
      >
        {t('replaceProductsDialog.confirmButtonText')}
      </LoadingButton>

      <Box height="8px" />

      <Button disableElevation color="primary" variant="text" onClick={onClose}>
        {t('replaceProductsDialog.cancelButtonText')}
      </Button>
    </StyledDialog>
  )
}

export default ReplaceProductDialog
